import * as S from './images.styles';

import React from 'react';
import {Node, Element, Editor, Transforms, Point, Range, Path} from 'slate';
import {BlockWrapper, useDrag} from './drag-drop';

import {
  RenderElementProps,
  useEditor,
  ReactEditor,
  useReadOnly,
  useSelected,
} from 'slate-react';
import {WBSlateReduxBridgeContext} from '../WBSlateReduxBridge';
import {useUploadMarkdownImage} from '../../../util/images';

export interface Image extends Element {
  type: 'image';
  href?: string; // href the image links to (if linked image)
  url?: string; // image src
  file?: File;
  hasCaption?: boolean;
}

export const isImage = (node: Node): node is Image => node.type === 'image';

export const ImageElement: React.FC<RenderElementProps & {element: Image}> = ({
  attributes,
  element,
  children,
}) => {
  const selected = useSelected();
  const editor = useEditor();
  const readOnly = useReadOnly();

  const {viewId} = React.useContext(WBSlateReduxBridgeContext);

  const {uploadImages, uploadState, setUploadState} = useUploadMarkdownImage(
    viewId
  );

  const [draggingImageOver, setDraggingImageOver] = React.useState(false);

  React.useEffect(() => {
    if (uploadState == null) {
      return;
    }

    const {url} = uploadState;

    if (url == null || element.url === url) {
      return;
    }

    Transforms.setNodes(
      editor,
      {url, file: undefined},
      {at: ReactEditor.findPath(editor, element)}
    );
  }, [editor, element, uploadState]);

  // from drag and drop or paste
  const {file} = element;
  React.useEffect(() => {
    if (file == null) {
      return;
    }

    uploadImages([file as File]);
  }, [file, uploadImages]);

  const {sourceAttributes, handleAttributes} = useDrag(element, {
    onHandleMouseDown() {
      if (readOnly) {
        return;
      }

      Transforms.select(editor, ReactEditor.findPath(editor, element));
    },
  });

  if (
    element.url == null ||
    uploadState?.uploading ||
    uploadState?.error != null
  ) {
    return (
      <BlockWrapper attributes={attributes} element={element}>
        <div contentEditable={false}>
          <S.ImagePlaceholderWrapper
            selected={selected}
            draggingImageOver={draggingImageOver}>
            <S.ImagePlaceholder>
              <S.ImagePlaceholderIcon />
              <span>
                {uploadState?.uploading
                  ? `Uploading ${uploadState.name}...`
                  : uploadState?.error != null
                  ? `Error: ${uploadState.error}`
                  : 'Drag an image here'}
              </span>
            </S.ImagePlaceholder>
            <S.ImagePlaceholderInput
              type="file"
              accept="image/x-png,image/gif,image/jpeg"
              onDragEnter={e => {
                // Hack for Safari because it doesn't support dataTransfer.items
                if (e.dataTransfer.items.length === 0) {
                  e.stopPropagation();
                  setDraggingImageOver(true);
                }

                // tslint:disable-next-line:prefer-for-of
                for (let i = 0; i < e.dataTransfer.items.length; i++) {
                  if (e.dataTransfer.items[i].type.includes('image')) {
                    e.stopPropagation();
                    setDraggingImageOver(true);
                    break;
                  }
                }
              }}
              onDragLeave={e => {
                if (draggingImageOver) {
                  e.stopPropagation();
                  setDraggingImageOver(false);
                }
              }}
              onDrop={e => {
                if (draggingImageOver) {
                  e.stopPropagation();
                  setDraggingImageOver(false);
                }
              }}
              onChange={e => {
                const {files} = e.target;

                if (files == null) {
                  return;
                }

                uploadImages(Array.from(files));
              }}></S.ImagePlaceholderInput>
          </S.ImagePlaceholderWrapper>
        </div>
        {children}
      </BlockWrapper>
    );
  }

  return (
    <BlockWrapper attributes={attributes} element={element}>
      <S.ImageOuterWrapper>
        <S.ImageWrapper>
          <div contentEditable={false}>
            {(() => {
              const content = (
                <S.Image
                  {...sourceAttributes}
                  {...handleAttributes}
                  selected={selected}
                  src={element.url}
                  onError={() => {
                    setUploadState({
                      error: "This image couldn't be loaded.",
                      uploading: false,
                      name: 'Failed to load',
                    });
                  }}
                />
              );

              if (element.href == null) {
                return content;
              } else {
                return (
                  <S.LinkedImageWrapper href={element.href} target="_blank">
                    {content}
                  </S.LinkedImageWrapper>
                );
              }
            })()}
          </div>
          {element.hasCaption ? (
            <S.ImageCaption
              empty={Editor.isEmpty(editor, element)}
              selected={selected}>
              {children}
            </S.ImageCaption>
          ) : (
            <>
              {selected && (
                <S.AddCaptionButton
                  contentEditable={false}
                  onMouseDown={e => {
                    e.preventDefault();
                    Transforms.setNodes(
                      editor,
                      {hasCaption: true},
                      {at: ReactEditor.findPath(editor, element)}
                    );
                  }}>
                  Write a caption...
                </S.AddCaptionButton>
              )}
              {children}
            </>
          )}
        </S.ImageWrapper>
      </S.ImageOuterWrapper>
    </BlockWrapper>
  );
};

export const withImages = <T extends ReactEditor>(editor: T) => {
  const {isVoid, insertData, insertBreak, deleteBackward} = editor;

  editor.isVoid = element => {
    return isImage(element) ? !element.hasCaption : isVoid(element);
  };

  editor.insertData = data => {
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < data.files.length; i++) {
      const file = data.files[i];
      if (file.type.includes('image')) {
        EditorWithImages.insertImageFromData(editor, file);
        return;
      }
    }

    insertData(data);
  };

  editor.insertBreak = () => {
    const imageEntry = Editor.above(editor, {match: n => isImage(n)});
    if (imageEntry != null && imageEntry[0].hasCaption) {
      Transforms.splitNodes(editor, {always: true});
      Transforms.setNodes(editor, {type: 'paragraph'});
      return;
    }

    insertBreak();
  };

  editor.deleteBackward = (...args) => {
    const imageEntry = Editor.above(editor, {match: n => isImage(n)});

    if (imageEntry != null && Editor.isEmpty(editor, imageEntry[0])) {
      Transforms.unsetNodes(editor, ['hasCaption'], {at: imageEntry[1]});

      // hacky selection reset because setting voids breaks Slate's selection synchronization
      Transforms.deselect(editor);
      window.setTimeout(() => {
        Transforms.select(editor, imageEntry[1]);
      });
      return;
    }

    deleteBackward(...args);
  };

  return editor;
};

interface EditorWithImagesInterface {
  insertImageFromData(
    editor: ReactEditor,
    file: File,
    options?: {
      at?: Point | Range | Path | undefined;
      clone?: boolean;
    }
  ): void;
}

export const EditorWithImages: EditorWithImagesInterface = {
  insertImageFromData(editor, file, options = {}) {
    const {clone = true, ...insertOptions} = options;
    Transforms.insertNodes(
      editor,
      {
        type: 'image',
        children: [{text: ''}],
        file: clone ? new File([file], file.name, {type: file.type}) : file,
      },
      insertOptions
    );
  },
};
